import type { MenuRowDef } from '@/components/app/editor/context-menus/menu-action-rows'
import { OBJECT_ACTION_ROWS } from '@/components/app/editor/context-menus/object-context-menu'
import {
  PHOTO_ACTION_DEFINITIONS,
  PHOTO_PLACEHOLDER_ACTION_IDS,
} from '@/lib/photos/photo-action-items'
import type { PhotoMenuActionId } from '../types/photo-menu-action-id'

/**
 * Where the menu's separators fall among the photo actions, per the design: Edit/Effects/Crop/Tag,
 * then Clipping path/Remove background, then Caption/Remove photo. The panel and toolbar show the
 * same actions as one flat list, so the grouping lives here, not in `PHOTO_ACTION_DEFINITIONS`.
 */
const SEPARATOR_AFTER: ReadonlySet<PhotoMenuActionId> = new Set(['tagPhoto', 'removeBackground'])

/**
 * The photo actions, in the order and with the labels of `PHOTO_ACTION_DEFINITIONS`, so the menu,
 * the Photo Actions panel and the photo toolbar cannot drift apart (WMPCM-9864 AC4/AC9). Like the
 * panel, an action with no handler is greyed out unless it is a placeholder that isn't wired yet.
 */
const PHOTO_ACTION_ROWS: ReadonlyArray<MenuRowDef<PhotoMenuActionId>> =
  PHOTO_ACTION_DEFINITIONS.flatMap(({ id, label }): MenuRowDef<PhotoMenuActionId>[] => {
    const row: MenuRowDef<PhotoMenuActionId> = {
      id,
      label,
      whenNoHandler: PHOTO_PLACEHOLDER_ACTION_IDS.has(id) ? 'enabled' : 'disabled',
    }

    return SEPARATOR_AFTER.has(id) ? [row, 'separator'] : [row]
  })

/** The photo menu, top to bottom: the photo actions, Text on Path, then the shared object rows. */
export const PHOTO_MENU_ROWS: ReadonlyArray<MenuRowDef<PhotoMenuActionId>> = [
  ...PHOTO_ACTION_ROWS,
  'separator',
  { id: 'textOnPath', label: 'Text on Path', whenNoHandler: 'disabled' },
  'separator',
  ...OBJECT_ACTION_ROWS,
]
